// PAYEXPORT1 (docs/build-briefs/PAYEXPORT1-gusto-tips-connection.md; DEC-514 item 2). Settings > Connections > Payroll (Gusto): this
// location's direct connection to Gusto (connect, disconnect, status), matching its people to Gusto people, and sending a pay
// period's card tips with a preview first. Admin surface: a pointer and keyboard are fine, every control is 44px or taller.
//
// The location is the signed-in device's; nothing here sends a location, a company or a token. The browser is only ever sent
// to the address the server returns for Connect, and only when it is an https page (or the simulated return address). Every
// word the server says about a problem is shown as written. All text is set with textContent.

import {
  getPayrollGusto,
  getPayrollPayPeriods,
  getPayrollPeople,
  getPayrollPreview,
  getPayrollSends,
  postPayrollAutoMatch,
  postPayrollConnect,
  postPayrollDisconnect,
  postPayrollSend,
  putPayrollMatch,
  type PayrollPeopleDto,
  type PayrollPeriodDto,
  type PayrollPreviewDto,
  type PayrollSendDto,
  type PayrollStatusDto,
} from "./api.ts";
import { usDate } from "./dateText.ts";
import { formatMoney } from "./reportFormat.ts";

export interface PayrollSectionDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  isCurrent: () => boolean;
  /** Takes the browser to the address the server named. */
  navigate: (url: string) => void;
}

export interface PayrollSection {
  refresh(): Promise<void>;
  /** The browser came back from Gusto (`?gusto=connected` or `?gusto=error&reason=...`). */
  handleArrival(outcome: "connected" | "error", reason: string | null): Promise<void>;
  reset(): void;
  /** True while the card is on screen: the feature is on and this person may see it. */
  isOffered(): boolean;
}

type Tone = "good" | "warn" | "bad" | "neutral";

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

function badge(tone: Tone, label: string, testId?: string): HTMLElement {
  const el = node("span", `so-badge so-badge-${tone}`);
  el.append(node("span", "so-badge-dot"), label);
  if (testId !== undefined) el.dataset["testid"] = testId;
  return el;
}

function button(
  label: string,
  className: string,
  testId: string,
  onClick: () => void,
  disabled = false,
): HTMLButtonElement {
  const el = node("button", className, label);
  el.type = "button";
  el.dataset["testid"] = testId;
  el.disabled = disabled;
  el.addEventListener("click", onClick);
  return el;
}

function messageOf(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "Something went wrong. Try again.";
}

/** "Sep 28 to Oct 11" from two calendar days. */
export function periodText(start: string, end: string): string {
  const short = (iso: string): string => usDate(iso).replace(/, \d{4}$/, "");
  return `${short(start)} to ${short(end)}`;
}

const ARRIVAL_ERRORS: Record<string, string> = {
  denied: "Gusto was not given access, so nothing was connected.",
  state_invalid: "That connection link expired or was already used. Connect again.",
  failed: "Gusto could not be connected. Try again.",
};

export function mountPayrollSection(root: HTMLElement, deps: PayrollSectionDeps): PayrollSection {
  let status: PayrollStatusDto | undefined;
  let people: PayrollPeopleDto | undefined;
  let periods: PayrollPeriodDto[] = [];
  let periodsError = "";
  let selectedRef = "";
  let preview: PayrollPreviewDto | undefined;
  let previewError = "";
  let sends: PayrollSendDto[] = [];
  let notice = "";
  let errorLine = "";
  let busy = false;
  let confirmingDisconnect = false;
  let sequence = 0;
  let reconnectSeen = false;
  root.hidden = true;

  function reset(): void {
    sequence += 1;
    status = undefined;
    people = undefined;
    periods = [];
    periodsError = "";
    selectedRef = "";
    preview = undefined;
    previewError = "";
    sends = [];
    notice = "";
    errorLine = "";
    busy = false;
    confirmingDisconnect = false;
    root.replaceChildren();
    root.hidden = true;
  }

  const isReconnect = (body: unknown): boolean =>
    (body as { error?: unknown } | null)?.error === "reconnect_needed";

  async function loadAll(retried = false): Promise<void> {
    const mine = (sequence += 1);
    reconnectSeen = false;
    const result = await getPayrollGusto(deps.apiBaseUrl(), deps.tenantId());
    if (mine !== sequence || !deps.isCurrent()) return;
    if (result.status !== 200 || typeof result.body.canManage !== "boolean") {
      // Off for this deployment, or this person may not see it: nothing to show, nothing to say.
      status = undefined;
      root.replaceChildren();
      root.hidden = true;
      return;
    }
    status = result.body;
    errorLine = "";
    if (status.connection.state === "connected") {
      const jobs: Promise<void>[] = [];
      if (status.canManage) jobs.push(loadPeople(mine));
      if (status.canSend) jobs.push(loadPeriods(mine));
      await Promise.all(jobs);
      if (mine !== sequence || !deps.isCurrent()) return;
      // A refused refresh is found by the call that made it; read the connection again so the card says so.
      if (reconnectSeen && !retried) return loadAll(true);
    } else {
      people = undefined;
      periods = [];
      preview = undefined;
    }
    render();
  }

  async function loadPeople(mine: number): Promise<void> {
    const result = await getPayrollPeople(deps.apiBaseUrl(), deps.tenantId());
    if (mine !== sequence) return;
    people = result.status === 200 ? result.body : undefined;
    if (result.status !== 200) errorLine = messageOf(result.body);
    if (isReconnect(result.body)) reconnectSeen = true;
  }

  async function loadPeriods(mine: number): Promise<void> {
    const [list, log] = await Promise.all([
      getPayrollPayPeriods(deps.apiBaseUrl(), deps.tenantId()),
      getPayrollSends(deps.apiBaseUrl(), deps.tenantId()),
    ]);
    if (mine !== sequence) return;
    periodsError = list.status === 200 ? "" : messageOf(list.body);
    if (isReconnect(list.body)) reconnectSeen = true;
    periods = list.status === 200 ? list.body.periods : [];
    sends = log.status === 200 ? log.body.sends : [];
    if (!periods.some((period) => period.payrollRef === selectedRef))
      selectedRef = periods[0]?.payrollRef ?? "";
    preview = undefined;
    previewError = "";
    if (selectedRef !== "") await loadPreview(mine);
  }

  async function loadPreview(mine: number): Promise<void> {
    const result = await getPayrollPreview(deps.apiBaseUrl(), deps.tenantId(), selectedRef);
    if (mine !== sequence) return;
    preview = result.status === 200 ? result.body : undefined;
    previewError = result.status === 200 ? "" : messageOf(result.body);
  }

  async function run(action: () => Promise<void>): Promise<void> {
    if (busy) return;
    busy = true;
    errorLine = "";
    notice = "";
    render();
    try {
      await action();
    } finally {
      busy = false;
    }
    if (deps.isCurrent()) render();
  }

  async function connect(): Promise<void> {
    await run(async () => {
      const result = await postPayrollConnect(deps.apiBaseUrl(), deps.tenantId());
      if (!deps.isCurrent()) return;
      if (result.status === 200 && typeof result.body.url === "string") {
        deps.navigate(result.body.url);
        return;
      }
      errorLine = messageOf(result.body);
    });
  }

  async function disconnect(): Promise<void> {
    confirmingDisconnect = false;
    await run(async () => {
      const result = await postPayrollDisconnect(deps.apiBaseUrl(), deps.tenantId());
      if (!deps.isCurrent()) return;
      if (result.status === 200) {
        notice = "Gusto is disconnected. Tips are no longer sent.";
        await loadAll();
      } else {
        errorLine = messageOf(result.body);
      }
    });
  }

  async function autoMatch(): Promise<void> {
    await run(async () => {
      const result = await postPayrollAutoMatch(deps.apiBaseUrl(), deps.tenantId());
      if (!deps.isCurrent()) return;
      if (result.status !== 200) {
        const message = messageOf(result.body);
        await loadAll();
        errorLine = message;
        return;
      }
      notice =
        result.body.matched === 0
          ? "No new people matched by email."
          : `${result.body.matched} matched by email.`;
      await loadAll();
    });
  }

  async function setMatch(employeeId: string, personRef: string | null): Promise<void> {
    await run(async () => {
      const result = await putPayrollMatch(
        deps.apiBaseUrl(),
        deps.tenantId(),
        employeeId,
        personRef,
      );
      if (!deps.isCurrent()) return;
      const message = result.status !== 200 ? messageOf(result.body) : "";
      await loadAll();
      if (message !== "") errorLine = message;
    });
  }

  async function choosePeriod(ref: string): Promise<void> {
    selectedRef = ref;
    await run(async () => {
      await loadPreview(sequence);
    });
  }

  async function send(): Promise<void> {
    await run(async () => {
      const result = await postPayrollSend(
        deps.apiBaseUrl(),
        deps.tenantId(),
        selectedRef,
        crypto.randomUUID(),
      );
      if (!deps.isCurrent()) return;
      if (result.status === 200 && result.body.send !== undefined) {
        notice =
          result.body.send.status === "sent"
            ? `Sent ${formatMoney(result.body.send.totalCents)} in card tips to Gusto.`
            : "";
        errorLine =
          result.body.send.status === "failed" ? (result.body.send.errorMessage ?? "") : "";
      } else {
        errorLine = messageOf(result.body);
      }
      const mine = sequence;
      const [list, log] = await Promise.all([
        getPayrollPayPeriods(deps.apiBaseUrl(), deps.tenantId()),
        getPayrollSends(deps.apiBaseUrl(), deps.tenantId()),
      ]);
      if (mine !== sequence) return;
      if (list.status === 200) periods = list.body.periods;
      if (log.status === 200) sends = log.body.sends;
      await loadPreview(mine);
    });
  }

  // ---------------------------------------------------------------------
  // Drawing
  // ---------------------------------------------------------------------

  function renderConnection(card: HTMLElement, current: PayrollStatusDto): void {
    const connection = current.connection;
    const head = node("div", "cp-location-head");
    const titles = node("div", "cp-location-titles");
    titles.append(node("div", "cp-location-name", connection.companyName ?? "Gusto"));
    if (connection.state !== "not_connected" && connection.connectedAt !== null) {
      const by = connection.connectedByName === null ? "" : ` by ${connection.connectedByName}`;
      const line = node(
        "div",
        "cp-location-account",
        `Connected${by} on ${usDate(connection.connectedAt)}`,
      );
      line.dataset["testid"] = "payroll-connected-line";
      titles.append(line);
    }
    const tone: Tone =
      connection.state === "connected"
        ? "good"
        : connection.state === "needs_reconnect"
          ? "warn"
          : "neutral";
    const label =
      connection.state === "connected"
        ? "Connected"
        : connection.state === "needs_reconnect"
          ? "Needs reconnecting"
          : "Not connected";
    const right = node("div", "cp-location-head-right");
    right.append(badge(tone, label, "payroll-state"));
    if (current.environment !== "production") {
      const test = node("span", "cp-tag", "Test connection");
      test.dataset["testid"] = "payroll-test-tag";
      right.append(test);
    }
    head.append(titles, right);
    card.append(head);
    if (connection.problem !== null) {
      const problem = node("p", "admin-error cp-error", connection.problem);
      problem.setAttribute("role", "alert");
      problem.dataset["testid"] = "payroll-problem";
      card.append(problem);
    }
    if (!current.canManage) return;
    const actions = node("div", "cp-actions");
    if (connection.state === "not_connected" || connection.state === "needs_reconnect") {
      actions.append(
        button(
          connection.state === "needs_reconnect" ? "Connect Gusto again" : "Connect Gusto",
          "btn-primary",
          "payroll-connect",
          () => void connect(),
          busy,
        ),
      );
    }
    if (connection.state !== "not_connected") {
      if (confirmingDisconnect) {
        actions.append(
          node("span", "cp-fact", "Disconnect Gusto from this location? Tips stop being sent."),
          button(
            "Disconnect",
            "btn-secondary",
            "payroll-disconnect-confirm",
            () => void disconnect(),
            busy,
          ),
          button("Keep connected", "btn-secondary", "payroll-disconnect-cancel", () => {
            confirmingDisconnect = false;
            render();
          }),
        );
      } else {
        actions.append(
          button(
            "Disconnect",
            "btn-secondary",
            "payroll-disconnect",
            () => {
              confirmingDisconnect = true;
              render();
            },
            busy,
          ),
        );
      }
    }
    card.append(actions);
  }

  function renderMatching(wrap: HTMLElement): void {
    if (people === undefined) return;
    wrap.append(node("div", "cp-heading", "Match people to Gusto"));
    wrap.append(
      node("p", "cp-fact", "People are matched by work email. Match anyone left over here."),
    );
    const list = node("ul", "cp-needs");
    list.dataset["testid"] = "payroll-matches";
    const free = people.unmatchedPeople;
    for (const staff of people.staff) {
      const row = node("li", "pg-row");
      row.dataset["testid"] = "payroll-match-row";
      row.append(node("span", "pg-name", staff.displayName));
      row.append(node("span", "pg-email", staff.email ?? "No email on file"));
      const select = node("select", "admin-select");
      select.dataset["testid"] = "payroll-match-select";
      select.setAttribute("aria-label", `Gusto person for ${staff.displayName}`);
      select.disabled = busy;
      const none = node("option", null, "Not matched");
      none.value = "";
      select.append(none);
      if (staff.match !== null) {
        const current = node(
          "option",
          null,
          staff.match.personName ?? "Matched, no longer listed in Gusto",
        );
        current.value = staff.match.personRef;
        select.append(current);
      }
      for (const person of free) {
        const option = node(
          "option",
          null,
          person.email === null ? person.name : `${person.name}, ${person.email}`,
        );
        option.value = person.ref;
        select.append(option);
      }
      select.value = staff.match?.personRef ?? "";
      select.addEventListener(
        "change",
        () => void setMatch(staff.employeeId, select.value === "" ? null : select.value),
      );
      row.append(select);
      if (staff.match !== null) {
        row.append(node("span", "cp-tag", staff.match.source === "email" ? "By email" : "By hand"));
      }
      list.append(row);
    }
    wrap.append(list);
    if (people.problem !== null) wrap.append(node("p", "admin-error cp-error", people.problem));
    wrap.append(
      (() => {
        const actions = node("div", "cp-actions");
        actions.append(
          button(
            "Match by email again",
            "btn-secondary",
            "payroll-auto-match",
            () => void autoMatch(),
            busy,
          ),
        );
        return actions;
      })(),
    );
  }

  function renderSend(wrap: HTMLElement): void {
    wrap.append(node("div", "cp-heading", "Send card tips to Gusto"));
    wrap.append(
      node(
        "p",
        "cp-fact",
        "Card tips only. Cash tips are not sent, and each person reports them to payroll.",
      ),
    );
    if (periodsError !== "") {
      const error = node("p", "admin-error cp-error", periodsError);
      error.setAttribute("role", "alert");
      wrap.append(error);
      return;
    }
    if (periods.length === 0) {
      wrap.append(
        node("p", "cp-fact", "No open pay periods in Gusto. Open payrolls will appear here."),
      );
      return;
    }
    const pick = node("select", "admin-select");
    pick.dataset["testid"] = "payroll-period-select";
    pick.setAttribute("aria-label", "Pay period");
    pick.disabled = busy;
    for (const period of periods) {
      const state = period.send?.status === "sent" ? " (sent)" : "";
      const option = node(
        "option",
        null,
        `${periodText(period.periodStart, period.periodEnd)}${state}`,
      );
      option.value = period.payrollRef;
      pick.append(option);
    }
    pick.value = selectedRef;
    pick.addEventListener("change", () => void choosePeriod(pick.value));
    wrap.append(pick);
    if (previewError !== "") {
      const error = node("p", "admin-error cp-error", previewError);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = "payroll-preview-error";
      wrap.append(error);
      return;
    }
    if (preview === undefined) return;
    for (const block of preview.blocks) {
      const line = node("p", "admin-error cp-error", block.message);
      line.dataset["testid"] = `payroll-block-${block.code}`;
      line.setAttribute("role", "alert");
      wrap.append(line);
      if (block.days !== undefined) {
        const days = node("ul", "cp-needs");
        days.dataset["testid"] = "payroll-open-days";
        for (const day of block.days.slice(0, 14)) days.append(node("li", "cp-need", usDate(day)));
        if (block.days.length > 14)
          days.append(node("li", "cp-need", `and ${block.days.length - 14} more`));
        wrap.append(days);
      }
    }
    if (preview.lines.length > 0) {
      const table = node("table", "pg-table");
      table.dataset["testid"] = "payroll-preview-table";
      const header = node("tr", null);
      for (const label of ["Employee", "Gusto person", "Days with tips", "Card tips"])
        header.append(node("th", null, label));
      table.append(header);
      for (const line of preview.lines) {
        const row = node("tr", null);
        row.dataset["testid"] = "payroll-preview-row";
        row.dataset["match"] = line.match.state;
        row.append(node("td", null, line.displayName));
        row.append(
          node(
            "td",
            line.match.state === "matched" ? null : "pg-warn",
            line.match.state === "matched"
              ? (line.match.personName ?? "")
              : line.match.state === "inactive"
                ? "No longer active in Gusto"
                : "Not matched",
          ),
        );
        row.append(node("td", "pg-num", String(line.daysWithTips)));
        row.append(node("td", "pg-num", formatMoney(line.amountCents)));
        table.append(row);
      }
      const total = node("tr", "pg-total");
      total.append(
        node(
          "td",
          null,
          `Total, ${preview.lines.length} ${preview.lines.length === 1 ? "person" : "people"}`,
        ),
        node("td", null),
        node("td", null),
      );
      total.append(node("td", "pg-num", formatMoney(preview.totalCents ?? 0)));
      table.append(total);
      wrap.append(table);
    }
    const footer = node("div", "cp-actions pg-footer");
    if (preview.closeoutTipsCents !== null) {
      footer.append(
        badge(
          preview.reconciled ? "good" : "bad",
          preview.reconciled
            ? `Matches closeout tips: ${formatMoney(preview.closeoutTipsCents)} for ${preview.period.dayCount} days`
            : `Does not match closeout tips: ${formatMoney(preview.closeoutTipsCents)}`,
          "payroll-reconciliation",
        ),
      );
    }
    const sendState = preview.send;
    if (preview.state === "sent" && sendState !== null) {
      footer.append(badge("good", `Sent ${formatMoney(sendState.totalCents)}`, "payroll-sent"));
    } else {
      const label = sendState?.status === "failed" ? "Send again" : "Send to Gusto";
      footer.append(
        button(
          label,
          "btn-primary",
          "payroll-send",
          () => void send(),
          busy || preview.state !== "ready",
        ),
      );
    }
    wrap.append(footer);
    if (preview.state === "sending")
      wrap.append(
        node("p", "cp-fact", "Tips for this pay period are being sent. Check back in a minute."),
      );
    if (sendState?.status === "failed" && sendState.errorMessage !== null) {
      const failed = node("p", "admin-error cp-error", sendState.errorMessage);
      failed.dataset["testid"] = "payroll-send-error";
      wrap.append(failed);
    }
    if (sends.length > 0) {
      wrap.append(node("div", "cp-heading", "Recent sends"));
      const list = node("ul", "cp-needs");
      list.dataset["testid"] = "payroll-sends";
      for (const entry of sends.slice(0, 5)) {
        const row = node("li", "cp-need");
        const what =
          entry.status === "sent"
            ? `Sent ${formatMoney(entry.totalCents)}`
            : entry.status === "failed"
              ? "Not sent"
              : "Sending";
        row.append(
          node("span", "cp-other-name", periodText(entry.periodStart ?? "", entry.periodEnd ?? "")),
          node(
            "span",
            "cp-fact",
            `${what}${entry.actorName === null ? "" : ` by ${entry.actorName}`}`,
          ),
        );
        if (entry.status === "failed" && entry.errorMessage !== null)
          row.append(node("span", "cp-fact", entry.errorMessage));
        list.append(row);
      }
      wrap.append(list);
    }
  }

  function render(): void {
    root.replaceChildren();
    if (status === undefined) {
      root.hidden = true;
      return;
    }
    root.hidden = false;
    const wrap = node("div", "cp-section");
    wrap.append(node("h2", "section-title", "Payroll (Gusto)"));
    if (notice !== "") {
      const line = node("p", "cp-status-line", notice);
      line.setAttribute("role", "status");
      line.dataset["testid"] = "payroll-notice";
      wrap.append(line);
    }
    const tone =
      status.connection.state === "connected"
        ? "good"
        : status.connection.state === "needs_reconnect"
          ? "warn"
          : "neutral";
    const card = node("div", `cp-location cp-location-${tone}`);
    card.dataset["testid"] = "payroll-connection";
    card.dataset["state"] = status.connection.state;
    renderConnection(card, status);
    if (errorLine !== "") {
      const error = node("p", "admin-error cp-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = "payroll-error";
      card.append(error);
    }
    wrap.append(card);
    if (status.connection.state === "connected") {
      if (status.canManage) renderMatching(wrap);
      if (status.canSend) renderSend(wrap);
    }
    root.append(wrap);
  }

  return {
    refresh: loadAll,
    async handleArrival(outcome, reason) {
      await loadAll();
      if (outcome === "connected") notice = "Gusto is connected.";
      else errorLine = ARRIVAL_ERRORS[reason ?? ""] ?? ARRIVAL_ERRORS["failed"] ?? "";
      if (status !== undefined) render();
    },
    reset,
    isOffered: () => !root.hidden,
  };
}
